<template>
  <v-card
    class="tarjeta-empresa"
    :class="{ 'tarjeta-pasable': puedeRotar }"
    @click="siguienteFoto"
  >
    <!-- FOTO: marco con proporcion fija para que el spinner ocupe el mismo
         alto que la imagen y la tarjeta no pegue un salto al cargar -->
    <div class="marco-foto">
      <div v-if="cargandoImagen" class="capa-carga">
        <v-progress-circular indeterminate color="primary" size="30" width="3" />
      </div>
      <!-- El fundido cruzado necesita que la que entra y la que sale convivan
           un instante, por eso van en position: absolute una encima de otra -->
      <transition v-else name="fundido">
        <v-img
          :key="indiceFoto"
          :src="foto"
          cover
          :aspect-ratio="16 / 9"
          class="foto"
          @load="yaPinto = true"
        >
          <!-- La promesa ya resolvio pero al navegador le falta pintar el
               base64. Sin esto se ve un hueco blanco en ese instante.
               En los cambios posteriores no gira nada: la foto ya esta en
               memoria y un spinner parpadeando cada pocos segundos molesta. -->
          <template #placeholder>
            <div class="capa-carga">
              <v-progress-circular
                v-if="!yaPinto"
                indeterminate
                color="primary"
                size="30"
                width="3"
              />
            </div>
          </template>
        </v-img>
      </transition>

      <div v-if="!cargandoImagen && fotosExtra > 0" class="cinta-fotos">
        <v-icon size="13">mdi-image-multiple-outline</v-icon>
        {{ indiceFoto + 1 }}/{{ imagenes.length }}
      </div>
      <div v-if="!cargandoImagen && !tieneFoto" class="cinta-fotos cinta-vacia">
        Sin fotos
      </div>
    </div>

    <!-- DATOS: llegan con la busqueda, no hay nada que esperar aqui -->
    <div class="cuerpo">
      <div class="titulo">{{ company.company }}</div>

      <div v-if="ubicacion" class="linea">
        <v-icon size="15">mdi-map-marker-outline</v-icon>
        <span>{{ ubicacion }}</span>
      </div>

      <p v-if="company.description" class="descripcion-empresa">
        {{ company.description }}
      </p>

      <div class="vistas">
        <v-icon size="15">mdi-eye-outline</v-icon>
        {{ company.views || 0 }} {{ company.views === 1 ? 'vista' : 'vistas' }}
      </div>
    </div>

    <div class="pie">
      <v-btn variant="text" class="accion" @click.stop="verEmpresa">
        <v-icon size="18">mdi-arrow-right</v-icon>
        Ver empresa
      </v-btn>
    </div>
  </v-card>
</template>

<script>
import apisCompanys from '@/apis/apisCompanys'
import imagenPorDefecto from '@/assets/imagen-none.jpg'

/**
 * Tarjeta publica de una empresa, la que se ve en /Companys.
 *
 * Los datos (nombre, ciudad, descripcion, vistas) ya vienen en la respuesta
 * paginada de la busqueda, asi que la tarjeta NO los vuelve a pedir: seria
 * una peticion por tarjeta para traer algo que ya esta en memoria, y esta es
 * la vista publica donde mas duele la lentitud.
 *
 * Lo que si carga cada tarjeta por su cuenta es SU imagen, que no viene en la
 * busqueda y viaja en base64. Cada una resuelve a su ritmo con su propio
 * spinner, asi que una empresa con foto pesada no deja la pagina en blanco.
 */
export default {
  name: 'ComponentCompany',
  props: {
    company: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      imagenes: [],
      cargandoImagen: true,
      indiceFoto: 0,
      yaPinto: false,
      temporizador: null
    }
  },
  computed: {
    tieneFoto() {
      return this.imagenes.length > 0
    },
    foto() {
      return this.tieneFoto ? this.imagenes[this.indiceFoto].pathBase64 : imagenPorDefecto
    },
    fotosExtra() {
      return Math.max(0, this.imagenes.length - 1)
    },
    puedeRotar() {
      return this.imagenes.length > 1
    },
    ubicacion() {
      return [this.company.city, this.company.country].filter(Boolean).join(', ')
    }
  },
  mounted() {
    apisCompanys.ImagesCompany(this.company.idCompany)
      .then(imagenes => {
        this.imagenes = imagenes || []
      })
      .catch(() => {
        // Que falle la foto no es motivo para romper la tarjeta: se queda
        // con la imagen de relleno y el resto se sigue viendo.
        this.imagenes = []
      })
      .finally(() => {
        this.cargandoImagen = false
        this.programarCambio()
      })

    document.addEventListener('visibilitychange', this.alCambiarVisibilidad)
  },
  beforeUnmount() {
    // Sin esto los temporizadores siguen corriendo despues de salir de la
    // vista: al cambiar de pagina quedarian siete relojes huerfanos.
    clearTimeout(this.temporizador)
    document.removeEventListener('visibilitychange', this.alCambiarVisibilidad)
  },
  methods: {
    /**
     * Programa el proximo cambio de foto con un tiempo AL AZAR entre 4 y 9
     * segundos, y lo vuelve a sortear en cada vuelta.
     *
     * El azar es lo importante: con un intervalo fijo las siete tarjetas
     * cambiarian todas a la vez y la pantalla entera parpadearia de golpe.
     * Sorteando cada vuelta ademas nunca se sincronizan con el tiempo, cosa
     * que si pasaria con intervalos fijos aunque fueran distintos.
     */
    programarCambio() {
      clearTimeout(this.temporizador)

      // Una sola foto no rota. Y si el usuario pidio menos animacion en su
      // sistema, no se le mueve nada: son fotos girando solas en pantalla.
      if (!this.puedeRotar || this.prefiereMenosMovimiento()) {
        return
      }

      const espera = 4000 + Math.random() * 5000

      this.temporizador = setTimeout(() => {
        // En una pestana de fondo no tiene sentido gastar en repintar; se
        // reanuda solo cuando el usuario vuelve.
        if (document.visibilityState === 'visible') {
          this.indiceFoto = (this.indiceFoto + 1) % this.imagenes.length
        }
        this.programarCambio()
      }, espera)
    },
    /**
     * Pasar de foto a mano. Un clic en la tarjeta NO entra a la empresa: para
     * eso esta el boton. Aqui solo se cambia la foto, asi que quien quiera ver
     * el sitio puede repasar las fotos sin salir del listado.
     */
    siguienteFoto() {
      if (!this.puedeRotar) {
        return
      }

      this.indiceFoto = (this.indiceFoto + 1) % this.imagenes.length

      // Reinicia la cuenta: si no, la foto que el usuario acaba de elegir
      // podria cambiarse sola medio segundo despues.
      this.programarCambio()
    },
    prefiereMenosMovimiento() {
      return window.matchMedia
        && window.matchMedia('(prefers-reduced-motion: reduce)').matches
    },
    alCambiarVisibilidad() {
      if (document.visibilityState === 'visible') {
        this.programarCambio()
      } else {
        clearTimeout(this.temporizador)
      }
    },
    verEmpresa() {
      this.$router.push({
        name: 'ViewCompanyView',
        params: { id: this.company.idCompany }
      })
    }
  }
}
</script>

<style scoped>
.tarjeta-empresa {
  display: flex;
  flex-direction: column;
  height: 100%;
  border-radius: 12px;
  overflow: hidden;
  background: #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.28);
  transition: box-shadow 0.2s ease, transform 0.2s ease;
}
.tarjeta-empresa:hover {
  transform: translateY(-3px);
  box-shadow: 0 10px 22px rgba(0, 0, 0, 0.4);
}
/* La mano solo cuando el clic hace algo, o sea cuando hay mas de una foto */
.tarjeta-pasable {
  cursor: pointer;
}

/* FOTO */
.marco-foto {
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  /* Sin esto el aspect-ratio se lo come el min-height: auto del flex en
     columna y el marco toma el alto natural de la imagen */
  min-height: 0;
  background: #eceff1;
}
/* Absoluta para que durante el fundido la foto que entra y la que sale
   ocupen el mismo sitio en vez de ponerse una debajo de la otra */
.foto {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
}

/* Fundido cruzado entre fotos */
.fundido-enter-active,
.fundido-leave-active {
  transition: opacity 0.7s ease;
}
.fundido-enter-from,
.fundido-leave-to {
  opacity: 0;
}
.capa-carga {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #eceff1;
}
.cinta-fotos {
  position: absolute;
  right: 8px;
  bottom: 8px;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: 10px;
  background: rgba(0, 0, 0, 0.6);
  color: #fff;
  font-size: 0.7rem;
  font-weight: 600;
}
.cinta-vacia {
  right: auto;
  left: 8px;
  font-weight: 500;
  letter-spacing: 0.03em;
}

/* CUERPO */
.cuerpo {
  flex: 1;
  padding: 12px 14px 10px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.titulo {
  font-size: 1.02rem;
  font-weight: 700;
  color: #1a1a1a;
  line-height: 1.25;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.linea {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 0.8rem;
  color: #667085;
}
.linea span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.descripcion-empresa {
  margin: 4px 0 0;
  font-size: 0.82rem;
  color: #475467;
  line-height: 1.35;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.vistas {
  margin-top: auto;
  padding-top: 8px;
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 0.76rem;
  color: #98a2b3;
}

/* PIE */
.pie {
  border-top: 1px solid #eceff1;
}
.accion {
  width: 100%;
  border-radius: 0;
  text-transform: none;
  font-weight: 600;
  letter-spacing: 0;
  color: #1976d2;
  gap: 6px;
}
</style>
